// Copyright (C) 2021 Bystronic Laser AG

import axios, { AxiosRequestHeaders } from 'axios';
import { BaseUrl } from '@/models/constants';
import { ListOrObject } from '@/models/metrics/listOrObject';
import { AlertOccurrence, AlertOccurrenceDTO } from '@/models/alertOccurrence';
import { WidgetEnum } from '@/models/enums/WidgetEnum';
import { RequestCanceledError } from '@/services/requestCanceledError';
import { FilterTimeSpanEnum } from '@/models/enums/FilterTimeSpanEnum';
import { FilterTimeAxisSpanEnum } from '@/models/enums/FilterTimeAxisSpanEnum';
import { isEmpty } from '@/utils/misc';
import {
  CutDeviceCurrentWorkloadData,
  CutDeviceCurrentWorkloadResponseEntry,
  CutOrTubesIntradayAvailabilityDeviceData,
  ImportantMessageLevel,
  CutOrTubesDeviceGanttStatus,
  SalesTopBySalesData,
} from '@/models/Charts/chartsData';
import { Memoize } from 'typescript-memoize';
import { AggregateEnum } from '@/models/enums/AggregateEnum';
import { FunctionMetric } from '@/models/enums/FunctionMetric';
import { PlanState } from '@/models/enums/PlanState';
import { SortOrder } from '@/models/enums/SortOrder';
import { devicesService } from '@/services/devices.service';
import { responseHandler } from './response.handler';
import { BendingPlanDetailData } from '@/views/workcenterConsole/BendIntradayAvailabilityTable/BendingPlanDetailData';

// The ignored fields are used by TechnologyConsoleTenantDevices through
// TechnologyTenantDevicesTableItem.
//
// Related data structures:
// - TechnologyTenantDevicesTableItem
export interface DeviceVersion {
  deviceid: string | null;
  tenantid: number | null; // Ignored
  FT_Material: string | null; // Ignored
  FT_Serial: string | null; // Ignored
  SW_Version: string | null;
  BeamShaperSerial: string | null; // Ignored
  ByMotionVersion: string | null;
  ByTransVersion: string | null;
  ByVisionVersion: string | null;
  CuttingHeadVersion: string | null; // Ignored
  MachineType: string | null;
  CuttingHeadType: string | null;
  EquipmentNumber: string | null;
}

interface DevicesMetricsFilter {
  tenantIdDh: number;
  deviceIds: string[];
  shifts: number[];
  timeSpan: FilterTimeSpanEnum;
  timeAxisSpan: FilterTimeAxisSpanEnum;
  startDate: string;
  endDate: string;
  statesOnly: boolean;
  planStates: PlanState[];
}

interface SMBSMetricsBCFilter {
  startDate: string;
  endDate: string;
  customerNo: string;
  axisTimespan: FilterTimeAxisSpanEnum;
  paramNumber: number;
  date: string;
  startAmount: number;
  endAmount: number;
  groupNumber: number;
  target: number;
  belowTarget: boolean;
  basedOnHistoricalData: boolean;
}

interface SMBSMetricsSFCFilter {
  startDate: string;
  endDate: string;
  axisTimespan: FilterTimeAxisSpanEnum;
  paramNumber: number;
}

export interface SSCMetricsFilter {
  dateFrom: string;
  dateTo: string;
  dateGrouping: FilterTimeAxisSpanEnum;

  modules: string[];
  messageLevel: number;
  eventCodes: string[];

  pageNumber: number;
  rowsPerPage: number;

  sortField: string;
  sortOrder: SortOrder;

  metric: FunctionMetric;

  last24h: boolean;

  historical: boolean;

  aggregate: AggregateEnum;

  versionInformation: boolean;
  interruptIfV4: boolean;
}

interface BendMetricsFilter {
  customerIdDh: number;
  deviceStringIds: string[];
  shiftIds: number[];
  startDate?: string;
  endDate?: string;
  timeSpan?: FilterTimeSpanEnum;
  partGuid?: string;
  intervalId?: number;
  pageNumber?: number;
  rowsPerPage?: number;
  dateGrouping?: FilterTimeAxisSpanEnum;
}

interface WebMetricsFilter {
  deviceId: string;
  startDate: string;
  endDate: string;
}

export interface CutOrTubesIntradayAvailabilityResponse {
  deviceid: string;
  startTimestamp: number;
  endTimestamp: number;
  state: CutOrTubesDeviceGanttStatus;
}

class MetricsService {
  async getDevicesMetrics<T>(
    kpiName: string,
    filter: Partial<DevicesMetricsFilter>,
    controller: AbortController,
    headers?: AxiosRequestHeaders,
  ): Promise<ListOrObject<T>> {
    return await this.getMetric<T>(
      'devices',
      {
        ...filter,
        kpiName,
      },
      controller,
      headers,
    );
  }

  /**
   * Returns the current workload data from the specified devices.
   *
   * Includes some information from the device, cutting plans and messages.
   *
   * @param deviceIds List of string ids from the devices to get the workload data.
   * @param customerIdDh Data hub id of the customer owning the devices.
   * @param planStates List of states in which the returned plans must be.
   * @param controller Controller to signal the abortion of the request.
   */
  async getDevicesCurrentWorkload(
    deviceIds: string[],
    customerIdDh: number,
    planStates: PlanState[],
    controller: AbortController,
  ): Promise<CutDeviceCurrentWorkloadData[]> {
    const response = await this.getDevicesMetrics<
      CutDeviceCurrentWorkloadResponseEntry[]
    >(
      WidgetEnum.LaserCurrentWorkload,
      {
        deviceIds,
        tenantIdDh: customerIdDh,
        planStates,
      },
      controller,
      // Do not cache the request to avoid incongruities when data is
      // received in streaming and is not shown aggregated
      {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
        Expires: '0',
      },
    );

    return response.map((responseEntry) => {
      const device = devicesService.store.getByStringId(
        responseEntry.deviceId,
      )!;
      return {
        ...responseEntry,
        deviceName: device.name,
        deviceModel: device.deviceBaseModelName,
      };
    });
  }

  /**
   * Returns the current workload data from the specified device.
   *
   * Includes some information from the device, cutting plans and messages.
   *
   * @param deviceId String id of the device to get the workload.
   * @param customerIdDh Data hub id of the customer owning the device.
   * @param controller Controller to signal the abortion of the request.
   */
  async getDeviceCurrentWorkload(
    deviceId: string,
    customerIdDh: number,
    controller: AbortController,
  ): Promise<CutDeviceCurrentWorkloadData> {
    const devicesWorkloadData = await this.getDevicesCurrentWorkload(
      [deviceId],
      customerIdDh,
      [PlanState.Started],
      controller,
    );

    return devicesWorkloadData[0];
  }

  async getSMBSMetricsBC<T>(
    kpiName: string,
    customerIdDh: number,
    filter: Partial<SMBSMetricsBCFilter>,
    controller: AbortController,
  ): Promise<ListOrObject<T>> {
    return await this.getMetric<T>(
      'smbs-metrics-bc',
      {
        ...filter,
        kpiName,
        tenantIdDh: customerIdDh,
      },
      controller,
    );
  }

  @Memoize({
    expiring: 10_000, // milliseconds
    hashFunction: (
      customerIdDh: number,
      numberOfSalespeople: number,
      startDate?: string,
      endDate?: string,
    ) => `${customerIdDh}${numberOfSalespeople}${startDate}${endDate}`,
  })
  async getCachedTopSalespeopleData(
    customerIdDh: number,
    numberOfSalespeople: number,
    startDate: string,
    endDate: string,
    controller: AbortController,
  ): Promise<SalesTopBySalesData[] | null> {
    return this.getSMBSMetricsBC<SalesTopBySalesData[]>(
      WidgetEnum.SalesTopSalespeople,
      customerIdDh,
      {
        paramNumber: numberOfSalespeople,
        startDate,
        endDate,
      },
      controller,
    );
  }

  async getSMBSMetricsSFC<T>(
    kpiName: string,
    customerIdDh: number,
    filter: Partial<SMBSMetricsSFCFilter>,
    controller: AbortController,
  ): Promise<ListOrObject<T>> {
    return await this.getMetric<T>(
      'smbs-metrics-sfc',
      {
        ...filter,
        kpiName,
        tenantIdDh: customerIdDh,
      },
      controller,
    );
  }

  async getSSCMetrics<T>(
    kpiName: string,
    customerIdDh: number,
    deviceId: string,
    filter: Partial<SSCMetricsFilter>,
    controller: AbortController,
  ): Promise<ListOrObject<T>> {
    return await this.getMetric<T>(
      'ssc-metrics',
      {
        ...filter,
        kpiName,
        tenantIdDh: customerIdDh,
        deviceId,
      },
      controller,
    );
  }

  async getBendProductionPlanDetail(
    filter: Partial<BendMetricsFilter>,
    abortController: AbortController,
  ): Promise<BendingPlanDetailData> {
    return await this.getBendingDevicesMetrics<BendingPlanDetailData>(
      WidgetEnum.BendDetailedProductionPlan,
      filter,
      abortController,
    ).then((data) => responseHandler.handleDates(data));
  }

  async getBendingDevicesMetrics<T>(
    kpiName: string,
    filter: Partial<BendMetricsFilter>,
    controller: AbortController,
    headers?: AxiosRequestHeaders,
  ): Promise<ListOrObject<T>> {
    return await this.getMetric<T>(
      'bending-devices',
      {
        ...filter,
        kpiName,
      },
      controller,
      headers,
    );
  }

  async getAlertOccurrences(
    deviceId: string,
    startDate: string,
    endDate: string,
    abortController: AbortController,
  ): Promise<AlertOccurrence[]> {
    return (
      await this.getWebMetrics<AlertOccurrenceDTO[]>(
        WidgetEnum.CareAlertOccurrencesTable,
        {
          deviceId,
          startDate,
          endDate,
        },
        abortController,
      )
    ).map((alertOccurrence) =>
      this.fixAlertOccurrenceListFields(alertOccurrence),
    );
  }

  private fixAlertOccurrenceListFields(
    alertOccurrence: AlertOccurrenceDTO,
  ): AlertOccurrence {
    return {
      name: alertOccurrence.name,
      description: alertOccurrence.description,
      isActive: alertOccurrence.isActive,
      dateGrouping: alertOccurrence.dateGrouping,
      startTimeSpan: alertOccurrence.startTimeSpan,
      latestTimeSpan: alertOccurrence.latestTimeSpan,
      metric: alertOccurrence.metric,
      latestValue: alertOccurrence.latestValue,
      operator: alertOccurrence.operator,
      threshold: alertOccurrence.threshold,
      variable: alertOccurrence.variable,
      // Fixes these fields:
      eventSources: alertOccurrence.eventSources?.split(','),
      eventCodes: alertOccurrence.eventCodes?.split(','),
    };
  }

  async getWebMetrics<T>(
    kpiName: string,
    filter: Partial<WebMetricsFilter>,
    abortController: AbortController,
  ): Promise<ListOrObject<T>> {
    return await this.getMetric<T>(
      'web',
      {
        ...filter,
        kpiName,
      },
      abortController,
    );
  }

  async getDevicesInfo<T>(
    customerIdDh: number,
    controller: AbortController,
  ): Promise<ListOrObject<T>> {
    return await this.getMetric<T>(
      'devices-info',
      {
        tenantIdDh: customerIdDh,
      },
      controller,
    );
  }

  async getCutOrTubesIntradayAvailabilityData<T>(
    kpiName: string,
    filter: Partial<DevicesMetricsFilter>,
    controller: AbortController,
  ): Promise<CutOrTubesIntradayAvailabilityDeviceData[]> {
    const data = await this.getDevicesMetrics<
      CutOrTubesIntradayAvailabilityResponse[]
    >(kpiName, filter, controller);
    return this.groupCutOrTubesIntradayAvailabilityDataByDevice(
      data,
      filter.deviceIds!,
    );
  }

  private groupCutOrTubesIntradayAvailabilityDataByDevice(
    data: CutOrTubesIntradayAvailabilityResponse[],
    deviceIds: string[],
  ): CutOrTubesIntradayAvailabilityDeviceData[] {
    return deviceIds.map((deviceId) => ({
      deviceStringId: deviceId,
      ganttData: data
        .filter((dataItem) => dataItem.deviceid === deviceId)
        .map((dataItem) => ({
          // * 1000 received epoch time in seconds, but Date expects milliseconds
          startTimestamp: new Date(dataItem.startTimestamp * 1000),
          endTimestamp: new Date(dataItem.endTimestamp * 1000),
          value: dataItem.state,
        })),
    }));
  }

  private async getMetric<T>(
    method: string,
    params: any,
    controller: AbortController,
    headers?: AxiosRequestHeaders,
  ): Promise<ListOrObject<T>> {
    const response = await axios.get<ListOrObject<T>>(
      `${BaseUrl}/metrics/${method}`,
      {
        params,
        signal: controller.signal,
        headers,
      },
    );

    if (controller.signal.aborted) {
      throw new RequestCanceledError();
    }

    return response.data;
  }

  getEventTypeParameter(
    eventTypes: ImportantMessageLevel[],
  ): number | undefined {
    const numberOfLevels = 2; // There're just Error and Warning levels for the moment
    if (isEmpty(eventTypes) || eventTypes.length === numberOfLevels) {
      return undefined; // If not specified, the backend to returns all levels
    }

    if (eventTypes.includes(ImportantMessageLevel.Error)) {
      return 3;
    } else {
      return 2;
    }
  }
}

export const metricsService = new MetricsService();
